import React, { useEffect } from 'react';
import {
  FaXmark,
  FaCircleInfo,
  FaMoneyCheckDollar,
  FaTriangleExclamation,
  FaShield
} from 'react-icons/fa6';
import { getFlagUrl } from '../utils/languages';

const HelpModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-neutral-900/80 backdrop-blur-md flex items-center justify-center z-[100] animate-fade-in p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
    >
      <article
        className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-2xl mx-auto shadow-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-neutral-700"
        itemScope
        itemType="https://schema.org/Article"
      >
        <meta itemProp="datePublished" content="2026-01-01" />
        <meta itemProp="dateModified" content="2026-03-07" />

        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/50">
          <div>
            <h2
              id="help-title"
              className="text-xl font-bold text-gray-900 dark:text-white flex items-center"
              itemProp="headline"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 mr-3 shadow-sm">
                <FaCircleInfo />
              </span>
              支票應該怎樣安全地填寫?
            </h2>
            <p className="mt-1 ml-[52px] text-xs text-gray-500 dark:text-gray-400">
              專業支票填寫指南 · 發布：
              <time itemProp="datePublished" dateTime="2026-01-01">
                2026 年 1 月 1 日
              </time>
              {' · '}最近更新：
              <time itemProp="dateModified" dateTime="2026-03-07">
                2026 年 3 月 7 日
              </time>
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-neutral-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-all"
            aria-label="關閉支票填寫指南"
          >
            <FaXmark className="text-xl" />
          </button>
        </div>

        <div
          className="p-6 overflow-y-auto custom-scrollbar bg-white dark:bg-neutral-800"
          itemProp="articleBody"
        >
          <div className="space-y-8">
            <section>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <FaShield className="text-green-500" />
                填寫支票時要遵守哪些核心原則?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                  <p className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-1">
                    一致性
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                    文字金額必須與數字金額完全吻合。若發生歧義，銀行通常以「文字大寫」為準。
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30">
                  <p className="text-sm font-bold text-amber-800 dark:text-amber-300 mb-1">
                    防篡改
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                    書寫文字時應緊貼起始線，並在結尾加上「正」或「Only」，不留任何空隙防止被添加數字。
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <FaMoneyCheckDollar className="text-primary-500" />
                不同地區的支票寫法有甚麼分別?
              </h3>
              <div className="rounded-xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-100 dark:bg-neutral-700/50 text-gray-700 dark:text-gray-200 font-bold">
                    <tr>
                      <th className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                        地區
                      </th>
                      <th className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                        貨幣單位與習慣
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-neutral-700 text-gray-600 dark:text-neutral-300">
                    <tr className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition-colors">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <img
                          src={getFlagUrl('hk')}
                          alt="香港區旗"
                          className="w-5 shadow-sm"
                        />
                        港澳地區
                      </td>
                      <td className="px-4 py-3">
                        繁體中文大寫，以「圓、角、仙」結尾，整數後必須加「正」。
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition-colors">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <img
                          src={getFlagUrl('cn')}
                          alt="中國國旗"
                          className="w-5 shadow-sm"
                        />
                        中國大陸
                      </td>
                      <td className="px-4 py-3">
                        簡體中文大寫，以「圆、角、分」結尾，整數後加「整」。
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition-colors">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <img
                          src={getFlagUrl('us')}
                          alt="美國國旗"
                          className="w-5 shadow-sm"
                        />
                        美國／國際
                      </td>
                      <td className="px-4 py-3">
                        英文格式，分位常表示為「and 50/100」，結尾加「Only」。
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition-colors">
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <img
                          src={getFlagUrl('gb')}
                          alt="英國國旗"
                          className="w-5 shadow-sm"
                        />
                        英國／歐洲
                      </td>
                      <td className="px-4 py-3">
                        使用「and」連接百位與十位，例如「One hundred and twenty」。
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-red-50 dark:bg-red-900/10 p-5 rounded-2xl border border-red-100 dark:border-red-900/30">
              <h3 className="text-base font-bold text-red-800 dark:text-red-400 mb-2 flex items-center gap-2">
                <FaTriangleExclamation />
                填寫支票時要避免哪些安全風險?
              </h3>
              <ul className="text-xs text-red-700 dark:text-red-300 space-y-2 list-disc ml-4 leading-relaxed">
                <li>
                  切勿在支票上留下任何塗改痕跡，大多數銀行會直接拒絕受理有塗改的支票。
                </li>
                <li>
                  數字「0」必須寫清楚，大寫「零」不可省略，以避免金額被擴充。
                </li>
                <li>
                  使用黑色或藍色原子筆書寫，避免使用易褪色或可擦拭的墨水。
                </li>
              </ul>
            </section>
          </div>
        </div>

        <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex justify-end bg-gray-50 dark:bg-neutral-800/50">
          <button
            onClick={onClose}
            className="px-8 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-bold shadow-lg shadow-primary-500/20 transition-all active:scale-95"
          >
            我已了解，開始使用
          </button>
        </div>
      </article>
    </div>
  );
};

export default HelpModal;